import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
// DTC-L: route OptiBay verification performed_at write through clock.
import { clock } from '@/utils/clock';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, Plus, Trash2, CheckCircle2, XCircle, ClipboardCheck, Sparkles } from 'lucide-react';
import {
  useSessionVerificationTests,
  useAddVerificationTest,
  useUpdateVerificationTest,
  useRemoveVerificationTest,
} from '@/hooks/useOptiBayVerification';
import { useSessionEvidence } from '@/hooks/useOptiBayEvidence';
import { generateVerificationSuggestions, type VerificationSuggestion } from '@/utils/optibay/generateGuidanceHints';
import type { OptiBaySession, OptiBaySessionVerificationTest } from '@/types/optibay';
import { cn } from '@/lib/utils';

interface OptiBayVerificationStepProps {
  session: OptiBaySession;
  onNext: () => void;
  onBack: () => void;
}

export function OptiBayVerificationStep({ session, onNext, onBack }: OptiBayVerificationStepProps) {
  const { data: tests = [] } = useSessionVerificationTests(session.id);
  const { data: evidence = [] } = useSessionEvidence(session.id);
  const addTest = useAddVerificationTest();
  const updateTest = useUpdateVerificationTest();
  const removeTest = useRemoveVerificationTest();

  const [showForm, setShowForm] = useState(false);
  const [testName, setTestName] = useState('');
  const [description, setDescription] = useState('');
  const [expectedResult, setExpectedResult] = useState('');

  const suggestions = generateVerificationSuggestions(evidence);

  const handleAdd = async () => {
    if (!testName.trim()) return;
    await addTest.mutateAsync({
      sessionId: session.id,
      testName: testName.trim(),
      description: description.trim() || undefined,
      expectedResult: expectedResult.trim() || undefined,
      sortOrder: tests.length,
    });
    setTestName('');
    setDescription('');
    setExpectedResult('');
    setShowForm(false);
  };

  const handleUseSuggestion = (suggestion: VerificationSuggestion) => {
    setTestName(suggestion.testName);
    setDescription(suggestion.description);
    setExpectedResult('');
    setShowForm(true);
  };

  const handleRecordResult = async (test: OptiBaySessionVerificationTest, passed: boolean) => {
    await updateTest.mutateAsync({
      id: test.id,
      updates: {
        passed,
        performed_at: clock.now().toISOString(),
      },
    });
  };

  const handleSetActualResult = async (test: OptiBaySessionVerificationTest, actual: string) => {
    await updateTest.mutateAsync({
      id: test.id,
      updates: { actual_result: actual },
    });
  };

  const completedCount = tests.filter(t => t.passed !== null).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Verification Tests</h4>
        <Badge variant="secondary" className="text-xs">
          {completedCount}/{tests.length} complete
        </Badge>
      </div>

      {/* Suggested tests from evidence analysis */}
      {suggestions.length > 0 && !showForm && (
        <div className="space-y-2">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            Suggested Tests
          </p>
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleUseSuggestion(s)}
              className="w-full text-left rounded-lg border border-primary/20 bg-primary/5 p-2.5 hover:bg-primary/10 transition-colors"
            >
              <p className="text-xs font-medium">{s.testName}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{s.description}</p>
            </button>
          ))}
        </div>
      )}

      {tests.length === 0 && !showForm && suggestions.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">
          No verification tests yet. Add tests to confirm your diagnostic findings.
        </p>
      ) : (
        <div className="space-y-2">
          {tests.map(test => (
            <div key={test.id} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <ClipboardCheck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-xs font-semibold">{test.test_name}</span>
                  {test.passed !== null && (
                    <Badge variant={test.passed ? 'default' : 'destructive'} className="text-[10px]">
                      {test.passed ? 'Pass' : 'Fail'}
                    </Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-destructive/70 hover:text-destructive"
                  onClick={() => removeTest.mutate({ id: test.id, sessionId: session.id })}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              {test.description && (
                <p className="text-xs text-muted-foreground pl-5">{test.description}</p>
              )}
              {test.expected_result && (
                <p className="text-xs pl-5">
                  <span className="text-muted-foreground">Expected:</span> {test.expected_result}
                </p>
              )}

              {test.passed === null ? (
                <div className="flex items-center gap-2 pl-5">
                  <Input
                    placeholder="Actual result (optional)…"
                    className="text-xs h-7 flex-1"
                    defaultValue={test.actual_result || ''}
                    onBlur={(e) => {
                      if (e.target.value !== (test.actual_result || '')) {
                        handleSetActualResult(test, e.target.value);
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-green-600 border-green-200 hover:bg-green-50"
                    onClick={() => handleRecordResult(test, true)}
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Pass
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
                    onClick={() => handleRecordResult(test, false)}
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    Fail
                  </Button>
                </div>
              ) : test.actual_result ? (
                <p className="text-xs pl-5">
                  <span className="text-muted-foreground">Actual:</span> {test.actual_result}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <div className="border rounded-lg p-3 space-y-2">
          <Input
            placeholder="Test name…"
            value={testName}
            onChange={e => setTestName(e.target.value)}
            className="text-xs h-8"
          />
          <Textarea
            placeholder="Description (optional)…"
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="text-xs min-h-[50px]"
          />
          <Input
            placeholder="Expected result (optional)…"
            value={expectedResult}
            onChange={e => setExpectedResult(e.target.value)}
            className="text-xs h-8"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleAdd} disabled={!testName.trim() || addTest.isPending}>
              Add Test
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs"
          onClick={() => setShowForm(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Verification Test
        </Button>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back
        </Button>
        <Button size="sm" onClick={onNext}>
          Continue to Interpretation
          <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
